import React from "react";
import ReactDOM from "react-dom/client";
import { createHashRouter, RouterProvider } from "react-router-dom";
import { I18nextProvider } from "react-i18next";
import { HelmetProvider } from "react-helmet-async";
import { initializeIcons } from "@fluentui/react";
import { ThemeProvider } from "./contexts/ThemeContext";

import "./index.css";

import Chat from "./pages/chat/Chat";
import Login from "./pages/login/Login";
import LayoutWrapper from "./layoutWrapper";
import AuthGuard from "./components/AuthGuard/AuthGuard";
import AgentAccessGuard from "./pages/agent/AgentAccessGuard";
import i18next from "./i18n/config";

initializeIcons();

const loadWorkflowBuilder = async () => ({
    Component: (await import("./features/workflowBuilder/WorkflowBuilderPage")).WorkflowBuilderPage
});

const router = createHashRouter([
    // PUBLIC routes (no authentication required)
    {
        path: "/",
        lazy: () => import("./pages/landing/LandingPage")
    },
    {
        path: "/login",
        element: <Login />
    },
    {
        path: "/test-login",
        element: <Login testLogin />
    },
    {
        path: "/invitation",
        lazy: () => import("./pages/invitation/InvitationPage")
    },
    {
        path: "/verify-email",
        lazy: () => import("./pages/verify-email/VerifyEmailPage")
    },
    {
        path: "/privacy",
        lazy: () => import("./pages/legal/PrivacyPolicy")
    },
    {
        path: "/terms",
        lazy: () => import("./pages/legal/TermsOfService")
    },
    {
        path: "/apply/:jobId",
        lazy: () => import("./pages/apply/ApplyPage")
    },
    // AUTHENTICATED routes (wrapped in AuthGuard)
    {
        path: "/app",
        element: <AuthGuard />,
        children: [
            {
                element: <LayoutWrapper />,
                children: [
                    {
                        index: true,
                        element: <Chat />
                    },
                    {
                        path: "chats/:chatId",
                        element: <Chat />
                    },
                    {
                        path: "workflows/new",
                        lazy: loadWorkflowBuilder
                    },
                    {
                        path: "workflows/:draftId",
                        lazy: loadWorkflowBuilder
                    },
                    // HR-specific routes MUST come before generic agent routes
                    {
                        path: "agent/hr/post-job",
                        element: <AgentAccessGuard restrictedAgent="hr" />,
                        children: [{ index: true, lazy: () => import("./pages/agent/hr/post-job/PostJobPage") }]
                    },
                    {
                        path: "agent/hr/jobs",
                        element: <AgentAccessGuard restrictedAgent="hr" />,
                        children: [{ index: true, lazy: () => import("./pages/agent/hr/manage-jobs/ManageJobsPage") }]
                    },
                    {
                        path: "agent/hr/manage",
                        element: <AgentAccessGuard restrictedAgent="hr" />,
                        children: [{ index: true, lazy: () => import("./pages/agent/hr/manage-jobs/ManageJobsPage") }]
                    },
                    {
                        path: "agent/hr/candidates",
                        element: <AgentAccessGuard restrictedAgent="hr" />,
                        children: [{ index: true, lazy: () => import("./pages/agent/hr/candidates/CandidatesPage") }]
                    },
                    // Generic agent routes — single entry with optional segments
                    // to prevent component remount when chatId changes.
                    {
                        path: "agent",
                        lazy: () => import("./pages/agent/AgentRouter")
                    },
                    {
                        path: "agent/:agentType/:chatId?",
                        lazy: () => import("./pages/agent/AgentRouter")
                    },
                    {
                        path: "search",
                        lazy: () => import("./pages/search/Search")
                    },
                    {
                        path: "workspace",
                        lazy: () => import("./pages/workspace/Workspace")
                    },
                    {
                        path: "meetings",
                        lazy: () => import("./pages/meetings/Meetings")
                    },
                    {
                        path: "meeting-recording",
                        lazy: () => import("./pages/meetings/MeetingRecording")
                    },
                    {
                        path: "meetings/:meetingId",
                        lazy: () => import("./pages/meetings/MeetingDetail")
                    },
                    {
                        path: "projects/:projectId",
                        lazy: () => import("./pages/project/Project")
                    },
                    {
                        path: "insights",
                        lazy: () => import("./pages/insights/InsightsReview")
                    },
                    {
                        path: "admin",
                        lazy: () => import("./pages/organization-admin/OrganizationAdminPage")
                    },
                    {
                        path: "admin-preview",
                        lazy: () => import("./pages/organization-admin/OrganizationAdminPage")
                    },
                    {
                        path: "*",
                        lazy: () => import("./pages/NoPage")
                    }
                ]
            }
        ]
    }
]);

ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
    <React.StrictMode>
        <I18nextProvider i18n={i18next}>
            <HelmetProvider>
                <ThemeProvider>
                    <RouterProvider router={router} />
                </ThemeProvider>
            </HelmetProvider>
        </I18nextProvider>
    </React.StrictMode>
);
